import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Header from "../Header/Header";
import BillsContainer from "../BillsContainer/BillsContainer";
import "./Home.css";

const PAGE_SIZE = 9;

const TYPE_TO_FILE = {
  all: "bills/bills.json",
  presdocu: "bills/presdocu_bills.json",
  rule: "bills/rule_bills.json",
  notice: "bills/notice_bills.json",
};

const resolvePublicPath = (path) => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base = process.env.PUBLIC_URL || "";
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

const parseBillDate = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const parseInputDate = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const Home = () => {
  const [selectedType, setSelectedType] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [keyword, setKeyword] = useState("");
  const [allBillFiles, setAllBillFiles] = useState([]);
  const [billFiles, setBillFiles] = useState([]);
  const [bills, setBills] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBill, setSelectedBill] = useState(null);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const billCacheRef = useRef(new Map());

  const fetchBillsBatch = useCallback(async (paths) => {
    if (!paths.length) {
      return [];
    }

    const results = await Promise.all(
      paths.map(async (path) => {
        const response = await fetch(resolvePublicPath(path));

        if (!response.ok) {
          throw new Error(`Failed to load bill data: ${path}`);
        }

        return response.json();
      })
    );

    return results;
  }, []);

  const ensureBillsInCache = useCallback(
    async (paths) => {
      const missing = paths.filter((path) => !billCacheRef.current.has(path));
      if (!missing.length) {
        return;
      }

      const fetched = await fetchBillsBatch(missing);
      missing.forEach((path, index) => {
        billCacheRef.current.set(path, fetched[index]);
      });
    },
    [fetchBillsBatch]
  );

  const getPageBills = useCallback(
    async (files, pageNumber) => {
      if (!files.length) {
        return [];
      }

      const start = (pageNumber - 1) * PAGE_SIZE;
      const slice = files.slice(start, start + PAGE_SIZE);

      if (!slice.length) {
        return [];
      }

      await ensureBillsInCache(slice);

      return slice
        .map((path) => billCacheRef.current.get(path))
        .filter(Boolean);
    },
    [ensureBillsInCache]
  );

  const filterFiles = useCallback(
    async (files, fromValue, toValue, keywordValue) => {
      const from = parseInputDate(fromValue);
      const to = parseInputDate(toValue);
      const normalizedKeyword = (keywordValue || "").trim().toLowerCase();
      const hasKeyword = Boolean(normalizedKeyword);

      if (!files.length || (!from && !to && !hasKeyword)) {
        return files;
      }

      await ensureBillsInCache(files);

      return files.filter((path) => {
        const bill = billCacheRef.current.get(path);
        if (!bill) {
          return false;
        }

        const billDate = parseBillDate(bill.date);
        if (!billDate) {
          return false;
        }

        if (from && billDate < from) {
          return false;
        }

        if (to && billDate > to) {
          return false;
        }

        if (hasKeyword) {
          const summaryText =
            typeof bill.summary === "string" ? bill.summary.toLowerCase() : "";

          if (!summaryText.includes(normalizedKeyword)) {
            return false;
          }
        }

        return true;
      });
    },
    [ensureBillsInCache]
  );

  const loadNews = useCallback(
    async (indexFile) => {
      setIsInitialLoading(true);
      setIsPageLoading(false);
      setBills([]);
      setBillFiles([]);
      setAllBillFiles([]);
      setCurrentPage(1);
      billCacheRef.current = new Map();

      try {
        const response = await fetch(resolvePublicPath(indexFile));

        if (!response.ok) {
          throw new Error("Unable to load bill index");
        }
        
        const data = await response.json();

        let files = []
        Object.entries(data).forEach(([, value]) => {
          files = files.concat(value)
        });
        
        setAllBillFiles(files);
      } catch (err) {
        console.error(err);
        setBills([]);
        setBillFiles([]);
        setAllBillFiles([]);
        setCurrentPage(1);
      } finally {
        setIsInitialLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const file = TYPE_TO_FILE[selectedType] || TYPE_TO_FILE.all;
    loadNews(file);
  }, [selectedType, loadNews]);

  useEffect(() => {
    let cancelled = false;

    const applyFilters = async () => {
      if (!allBillFiles.length) {
        setBillFiles([]);
        setBills([]);
        setCurrentPage(1);
        return;
      }

      setIsPageLoading(true);
      setBills([]);

      try {
        const filtered = await filterFiles(
          allBillFiles,
          fromDate,
          toDate,
          keyword
        );
        if (cancelled) {
          return;
        }

        setBillFiles(filtered);

        const initialBills = await getPageBills(filtered, 1);
        if (cancelled) {
          return;
        }

        setBills(initialBills);
        setCurrentPage(1);
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setBills([]);
          setBillFiles([]);
          setCurrentPage(1);
        }
      } finally {
        if (!cancelled) {
          setIsPageLoading(false);
        }
      }
    };

    applyFilters();

    return () => {
      cancelled = true;
    };
  }, [allBillFiles, fromDate, toDate, keyword, filterFiles, getPageBills]);

  const totalPages = useMemo(
    () => (billFiles.length ? Math.ceil(billFiles.length / PAGE_SIZE) : 0),
    [billFiles.length]
  );

  const showPagination = totalPages > 1;

  const handleTypeChange = (value) => {
    setSelectedType(value);
  };

  const handleFromDateChange = (value) => {
    if (value !== fromDate) {
      setFromDate(value);
    }
  };

  const handleToDateChange = (value) => {
    if (value !== toDate) {
      setToDate(value);
    }
  };

  const handleKeywordChange = (value) => {
    if (value !== keyword) {
      setKeyword(value);
    }
  };

  const handleToggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleClearFilters = useCallback(() => {
    setSelectedType("all");
    setFromDate("");
    setToDate("");
    setKeyword("");
  }, []);

  const handlePageChange = useCallback(
    async (pageNumber) => {
      if (!billFiles.length) {
        return;
      }

      const maxPage = Math.max(1, Math.ceil(billFiles.length / PAGE_SIZE));
      if (pageNumber < 1 || pageNumber > maxPage || pageNumber === currentPage) {
        return;
      }

      setIsPageLoading(true);
      setBills([]);

      try {
        const pageBills = await getPageBills(billFiles, pageNumber);
        setBills(pageBills);
        setCurrentPage(pageNumber);
      } catch (err) {
        console.error(err);
      } finally {
        setIsPageLoading(false);
      }
    },
    [billFiles, currentPage, getPageBills]
  );

  const handleNextPage = useCallback(() => {
    handlePageChange(currentPage + 1);
  }, [currentPage, handlePageChange]);

  const handlePreviousPage = useCallback(() => {
    handlePageChange(currentPage - 1);
  }, [currentPage, handlePageChange]);

  const handleBillClick = (bill) => {
    setSelectedBill(bill);
  };

  const handleCloseBillModal = () => {
    setSelectedBill(null);
  };

  const handleBackdropClick = (event, onClose) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const isLoading = isInitialLoading || isPageLoading;
  const isPrevDisabled = currentPage <= 1 || isLoading;
  const isNextDisabled =
    !totalPages || currentPage >= totalPages || isLoading;

  return (
    <div className="page">
      <div className="app-container">
        <Header
          selectedType={selectedType}
          fromDate={fromDate}
          toDate={toDate}
          keyword={keyword}
          isMenuOpen={isMenuOpen}
          onTypeChange={handleTypeChange}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
          onKeywordChange={handleKeywordChange}
          onToggleMenu={handleToggleMenu}
          onClearFilters={handleClearFilters}
        />

        <BillsContainer
          bills={bills}
          onBillClick={handleBillClick}
          isLoading={isLoading && bills.length === 0}
        />

        {showPagination && (
          <nav className="pagination" aria-label="Bills pagination">
            <button
              type="button"
              onClick={handlePreviousPage}
              disabled={isPrevDisabled}
              aria-label="Previous page"
            >
              ←
            </button>
            <span className="pagination-info">
              {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={isNextDisabled}
              aria-label="Next page"
            >
              →
            </button>
          </nav>
        )}
      </div>

      <button
        type="button"
        className="info-btn"
        onClick={() => setIsAboutOpen(true)}
        aria-label="About Bill Buddy AI"
      >
        i
      </button>

      {selectedBill && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          onClick={(event) => handleBackdropClick(event, handleCloseBillModal)}
        >
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="close-btn"
              onClick={handleCloseBillModal}
              aria-label="Close bill details"
            >
              X
            </button>
            <h2>{selectedBill.title}</h2>
            <p>
              <strong>Type:</strong> <span>{selectedBill.type}</span>
            </p>
            <p>
              <strong>Date:</strong> <span>{selectedBill.date}</span>
            </p>
            <p className="modal-summary">{selectedBill.summary}</p>
            <p>
              <a href={selectedBill.url} target="_blank" rel="noreferrer">
                Read Full PDF
              </a>
            </p>
          </div>
        </div>
      )}

      {isAboutOpen && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          onClick={(event) => handleBackdropClick(event, () => setIsAboutOpen(false))}
        >
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="close-btn"
              onClick={() => setIsAboutOpen(false)}
              aria-label="Close about modal"
            >
              X
            </button>
            <h2>About</h2>
            <p>
              Bill Buddy AI delivers clear, daily updates on some of the newest
              laws, bills, and Federal Reserve documents. We use advanced AI to
              summarize complex policies into simple, actionable insights — so
              you can stay informed without the jargon.
            </p>
            <img
              src="https://hitscounter.dev/api/hit?url=https%3A%2F%2Fgithub.com%2Fjoaopio-unigit&label=Visitors&icon=person-fill&color=%23198754&message=&style=flat&tz=UTC"
              alt="Visitor counter"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
